/**
 * Full-report section order (010): one ordered list of stable section ids
 * drives the PDF body, the Table of Contents and the builder list.
 *
 * Pure — no React, no react-pdf, no Supabase. CreateReport imports this
 * eagerly, and react-pdf must stay out of that chunk.
 *
 * The ids are persisted (report_data.section_order, brand_presets.layout.
 * section_order, reports.metadata.section_order): renaming one is a data
 * migration, not a refactor. See contracts/section-order.md.
 */
import type { BrandLayout } from '../../branding/types'

export type SectionId =
  | 'about_assessor'
  | 'executive_summary'
  | 'scope'
  | 'methodology'
  | 'site_map'
  | 'job_info'
  | 'vta'
  | 'traq'
  | 'results_summary'
  | 'health_vigor'
  | 'tree_inspections'
  | 'photos'
  | 'tpz_diagram'
  | 'observations'
  | 'tpp'
  | 'recommendations'
  | 'conclusions'
  | 'terms'
  | 'references'
  | 'additional_notes'
  | 'map_sheets'

export interface SectionDefinition {
  id: SectionId
  /** Builder label; the PDF title may differ by layout (see sectionTitle) */
  label: string
  /** 'written' has editor fields; 'generated' is built from job data */
  kind: 'written' | 'generated'
  /** The REPORT_SECTIONS entry it maps to (toggle, fields), when one exists */
  configId: string | null
  /** PDF heading and TOC title under the built-in layout */
  pdfTitle: string
}

/** The built-in default order (purpose full_assessment, photos inline) */
export const MOVABLE_SECTIONS: readonly SectionDefinition[] = [
  {
    id: 'about_assessor',
    label: 'About the Assessor',
    kind: 'generated',
    configId: null,
    pdfTitle: 'About the Assessor',
  },
  {
    id: 'executive_summary',
    label: 'Executive Summary',
    kind: 'written',
    configId: 'executive_summary',
    pdfTitle: 'Executive Summary',
  },
  {
    id: 'scope',
    label: 'Scope of Works',
    kind: 'written',
    configId: 'scope',
    pdfTitle: 'Scope of Works',
  },
  {
    id: 'methodology',
    label: 'Methodology',
    kind: 'written',
    configId: 'methodology',
    pdfTitle: 'Methodology',
  },
  {
    id: 'site_map',
    label: 'Site Overview Map',
    kind: 'generated',
    configId: 'site_map',
    pdfTitle: 'Site Overview Map',
  },
  {
    id: 'job_info',
    label: 'Job Information',
    kind: 'generated',
    configId: null,
    pdfTitle: 'Job Information',
  },
  {
    id: 'vta',
    label: 'Visual Tree Assessment',
    kind: 'written',
    configId: 'vta',
    pdfTitle: 'Visual Tree Assessment',
  },
  {
    id: 'traq',
    label: 'TRAQ Risk Assessment',
    kind: 'generated',
    configId: 'traq',
    pdfTitle: 'TRAQ Risk Assessment',
  },
  {
    id: 'results_summary',
    label: 'Results Summary',
    kind: 'generated',
    configId: null,
    pdfTitle: 'Results Summary',
  },
  {
    id: 'health_vigor',
    label: 'Health & Vigor Assessment',
    kind: 'written',
    configId: 'health_vigor',
    pdfTitle: 'Health & Vigor Assessment',
  },
  {
    id: 'tree_inspections',
    label: 'Individual Tree Inspections',
    kind: 'generated',
    configId: null,
    pdfTitle: 'Individual Tree Inspections',
  },
  { id: 'photos', label: 'Photos', kind: 'generated', configId: null, pdfTitle: 'Photo Plates' },
  {
    id: 'tpz_diagram',
    label: 'TPZ / CAD Diagram',
    kind: 'written',
    configId: 'tpz_diagram',
    pdfTitle: 'Tree Protection Zone Diagram',
  },
  {
    id: 'observations',
    label: 'Observations & Discussion',
    kind: 'written',
    configId: 'observations',
    pdfTitle: 'Observations & Discussion',
  },
  {
    id: 'tpp',
    label: 'Tree Protection Plan',
    kind: 'written',
    configId: 'tpp',
    pdfTitle: 'Tree Protection Plan',
  },
  {
    id: 'recommendations',
    label: 'Overall Recommendations',
    kind: 'written',
    configId: 'recommendations',
    pdfTitle: 'Overall Recommendations',
  },
  {
    id: 'conclusions',
    label: 'Conclusions',
    kind: 'written',
    configId: 'conclusions',
    pdfTitle: 'Conclusions',
  },
  {
    id: 'terms',
    label: 'Terms & Conditions',
    kind: 'written',
    configId: 'terms',
    pdfTitle: 'Terms & Conditions',
  },
  {
    id: 'references',
    label: 'References',
    kind: 'written',
    configId: 'references',
    pdfTitle: 'References',
  },
  {
    id: 'additional_notes',
    label: 'Additional Notes',
    kind: 'written',
    configId: 'additional_notes',
    pdfTitle: 'Additional Notes',
  },
  {
    id: 'map_sheets',
    label: 'Map Sheets',
    kind: 'generated',
    configId: null,
    pdfTitle: 'Appendix — Site Map Sheets',
  },
]

/** Fixed in place (FR-004): never in a stored order */
export const LOCKED_SECTIONS = ['cover', 'document_control', 'toc', 'declaration'] as const

const SECTION_BY_ID = Object.fromEntries(MOVABLE_SECTIONS.map((s) => [s.id, s])) as Record<
  SectionId,
  SectionDefinition
>

export const sectionDefinition = (id: SectionId): SectionDefinition => SECTION_BY_ID[id]

/** Move `id` to sit straight after `after` */
function placeAfter(order: SectionId[], id: SectionId, after: SectionId): SectionId[] {
  const rest = order.filter((x) => x !== id)
  rest.splice(rest.indexOf(after) + 1, 0, id)
  return rest
}

/**
 * Today's render sequence for this layout (FR-009): a summary moves the
 * methodology to an appendix after Additional Notes; appendix photo
 * placement puts the photo index after that.
 */
export function defaultSectionOrder(layout: Pick<BrandLayout, 'purpose' | 'photos'>): SectionId[] {
  let order = MOVABLE_SECTIONS.map((s) => s.id)
  if (layout.purpose === 'summary') order = placeAfter(order, 'methodology', 'additional_notes')
  if (layout.photos === 'appendix') {
    order = placeAfter(
      order,
      'photos',
      layout.purpose === 'summary' ? 'methodology' : 'additional_notes'
    )
  }
  return order
}

/** A stored order counts only as an array of strings; anything else is absent */
const isStoredOrder = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')

/**
 * Normalise a stored order (research R5). Unknown, locked and duplicate ids
 * are dropped (first occurrence kept); a default id missing from it goes in
 * after its nearest preceding default neighbour, or at the front. Always a
 * permutation of `defaultOrder`, and idempotent.
 */
export function resolveSectionOrder(stored: unknown, defaultOrder: SectionId[]): SectionId[] {
  if (!isStoredOrder(stored)) return [...defaultOrder]
  const known = new Set<string>(defaultOrder)
  const result: SectionId[] = []
  for (const id of stored) {
    if (known.has(id) && !result.includes(id as SectionId)) result.push(id as SectionId)
  }
  defaultOrder.forEach((id, i) => {
    if (result.includes(id)) return
    const neighbour = defaultOrder
      .slice(0, i)
      .reverse()
      .find((prev) => result.includes(prev))
    result.splice(neighbour ? result.indexOf(neighbour) + 1 : 0, 0, id)
  })
  return result
}

/** Report order → preset order → built-in default (research R3), normalised */
export function effectiveSectionOrder(args: {
  reportOrder: unknown
  presetOrder: unknown
  layout: Pick<BrandLayout, 'purpose' | 'photos'>
}): SectionId[] {
  const defaultOrder = defaultSectionOrder(args.layout)
  const stored = [args.reportOrder, args.presetOrder].find(isStoredOrder) ?? null
  return resolveSectionOrder(stored, defaultOrder)
}

const sameOrder = (a: SectionId[], b: SectionId[]) =>
  a.length === b.length && a.every((id, i) => id === b[i])

/** FR-006 write rule: nothing stored for the default order */
export function orderToPersist(next: SectionId[], defaultOrder: SectionId[]): SectionId[] | null {
  return sameOrder(next, defaultOrder) ? null : [...next]
}

/** Swap with the neighbour; a no-op at either end */
export function moveSection(order: SectionId[], id: SectionId, direction: -1 | 1): SectionId[] {
  const i = order.indexOf(id)
  const j = i + direction
  if (i < 0 || j < 0 || j >= order.length) return [...order]
  const next = order.filter((x) => x !== id)
  next.splice(j, 0, id)
  return next
}

/** PDF title for an entry under this layout, given its neighbours */
export function sectionTitle(
  id: SectionId,
  ctx: { layout: Pick<BrandLayout, 'purpose' | 'photos'>; order: SectionId[] }
): string {
  switch (id) {
    case 'methodology':
      return ctx.layout.purpose === 'summary' ? 'Appendix — Methodology' : 'Methodology'
    case 'photos':
      return ctx.layout.photos === 'appendix' ? 'Appendix — Photo Index' : 'Photo Plates'
    case 'map_sheets': {
      const i = ctx.order.indexOf('map_sheets')
      return i > 0 && ctx.order[i - 1] === 'site_map'
        ? 'Site Map Sheets'
        : 'Appendix — Site Map Sheets'
    }
    default:
      return sectionDefinition(id).pdfTitle
  }
}
